import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import exerciseChoicesApi from 'api/rest/exercises/choices';
import PATHS from 'constants/paths';
import QUERY from 'constants/queryKeys';
import { resolveSubjectNumericId } from 'services/common/subjectParsers/subjectParsers';
import useExercisesStore from 'storages/exercises/exercises';
import useSubjectStore from 'storages/subject';

const FiltersCountContext = createContext(null);

const { Provider } = FiltersCountContext;

const toOptionalId = value => {
  if (value === 'all' || value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const FiltersCountProvider = props => {
  const location = useLocation();
  const { subject } = useSubjectStore();
  const { section, subsection, curriculum } = useExercisesStore();
  const [filters, setFilters] = useState({});
  const subjectId = resolveSubjectNumericId(subject);
  const sectionId = toOptionalId(section);
  const subsectionId = toOptionalId(subsection);
  const onExercisesPage = PATHS.EXERCISES === location.pathname;

  const { data: markChoices, refetch } = useQuery(
    [QUERY.GET_MARK_CHOICES, subjectId, sectionId, subsectionId, curriculum],
    exerciseChoicesApi.getMark({
      subject: subjectId,
      section: sectionId,
      subsection: subsectionId,
      curriculum,
    }),
    { enabled: onExercisesPage && !!subjectId, keepPreviousData: true },
  );

  useEffect(() => {
    if (!markChoices) return;
    const userMarks = markChoices.map(({ value, label, count }) => ({
      name: label,
      id: value,
      exercisesCount: count,
    }));
    setFilters(prev => ({ ...prev, userMarks }));
  }, [markChoices]);

  const getMarksCount = useCallback(() => {
    if (onExercisesPage && subjectId) refetch();
  }, [onExercisesPage, subjectId, refetch]);

  return <Provider value={{ filters, updaters: { getMarksCount } }} {...props} />;
};

export const useFiltersCountContext = () => useContext(FiltersCountContext);

export default FiltersCountProvider;
